import React from 'react';
import { MockupPage, MockupSection, GeneratedMockup } from '../types/designmind';
import { 
  Layout, 
  Palette, 
  Type, 
  Eye, 
  Code, 
  ChevronRight, 
  ArrowRight,
  CheckCircle,
  Image as ImageIcon,
  ShoppingCart,
  MessageSquare,
  Star,
  TrendingUp,
  Users,
  Target,
  Zap
} from 'lucide-react';

interface MockupPreviewProps {
  mockup: GeneratedMockup;
  language: string;
}

const SectionIcon: React.FC<{ type: string }> = ({ type }) => {
  const iconMap: Record<string, React.ReactNode> = {
    header: <Layout className="w-4 h-4" />,
    hero: <Zap className="w-4 h-4" />,
    features: <CheckCircle className="w-4 h-4" />,
    testimonials: <MessageSquare className="w-4 h-4" />,
    pricing: <ShoppingCart className="w-4 h-4" />,
    footer: <Layout className="w-4 h-4" />,
    cta: <Target className="w-4 h-4" />,
    trust: <Users className="w-4 h-4" />,
    'product-grid': <ShoppingCart className="w-4 h-4" />,
    'product-detail': <ImageIcon className="w-4 h-4" />,
    faq: <MessageSquare className="w-4 h-4" />,
    stats: <TrendingUp className="w-4 h-4" />,
    steps: <ChevronRight className="w-4 h-4" />,
    timeline: <ArrowRight className="w-4 h-4" />
  };
  return <span className="text-indigo-400">{iconMap[type] || <Layout className="w-4 h-4" />}</span>;
};

const LayoutBadge: React.FC<{ layout: string }> = ({ layout }) => {
  const colors: Record<string, string> = {
    centered: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
    split: 'bg-purple-500/20 text-purple-300 border-purple-500/50',
    grid: 'bg-green-500/20 text-green-300 border-green-500/50',
    fullwidth: 'bg-orange-500/20 text-orange-300 border-orange-500/50',
    bento: 'bg-pink-500/20 text-pink-300 border-pink-500/50',
    marquee: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/50',
    timeline: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
    accordion: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/50',
    stats: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50'
  };
  
  return (
    <span className={`px-2 py-1 rounded-md text-xs font-semibold border ${colors[layout] || 'bg-slate-500/20 text-slate-300 border-slate-500/50'}`}>
      {layout}
    </span>
  );
};

const SectionPreview: React.FC<{ section: MockupSection; index: number }> = ({ section, index }) => {
  const getLayoutStyle = (layoutType: string) => {
    switch (layoutType) {
      case 'centered':
        return 'flex flex-col items-center justify-center text-center p-6';
      case 'split':
        return 'grid grid-cols-1 md:grid-cols-2 gap-6 p-6';
      case 'grid':
        return 'grid grid-cols-1 md:grid-cols-3 gap-4 p-6';
      case 'bento':
        return 'grid grid-cols-2 md:grid-cols-4 gap-3 p-6';
      case 'marquee':
        return 'flex items-center gap-4 overflow-x-auto p-6';
      case 'timeline':
        return 'flex flex-col gap-4 p-6';
      case 'accordion':
        return 'flex flex-col gap-2 p-6';
      case 'stats':
        return 'grid grid-cols-2 md:grid-cols-4 gap-4 p-6';
      default:
        return 'p-6';
    }
  };

  return (
    <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl mb-4 overflow-hidden">
      {/* Section Header */}
      <div className="bg-slate-900/50 border-b border-slate-700/50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SectionIcon type={section.type} />
          <div>
            <h4 className="text-sm font-bold text-white capitalize">{section.type.replace('-', ' ')}</h4>
            <p className="text-xs text-slate-400">{section.visualCue}</p>
          </div>
        </div>
        <LayoutBadge layout={section.layoutType} />
      </div>

      {/* Section Content Preview */}
      <div className={getLayoutStyle(section.layoutType)}>
        {section.headline && (
          <div className="mb-2">
            <h3 className="text-lg font-bold text-white mb-1">{section.headline}</h3>
            {section.subheadline && (
              <p className="text-sm text-slate-400">{section.subheadline}</p>
            )}
          </div>
        )}

        {section.content && section.content.length > 0 && (
          <div className="space-y-2">
            {section.content.slice(0, 3).map((item, idx) => (
              <div key={idx} className="bg-slate-900/30 rounded-lg p-3 text-sm text-slate-300">
                {item}
              </div>
            ))}
            {section.content.length > 3 && (
              <p className="text-xs text-slate-500">+{section.content.length - 3} more items</p>
            )}
          </div>
        )}

        {section.ctaText && (
          <div className="mt-4">
            <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg text-sm font-semibold transition-colors">
              {section.ctaText}
            </button>
            {section.secondaryCtaText && (
              <button className="ml-3 bg-slate-700 hover:bg-slate-600 text-white px-6 py-2 rounded-lg text-sm font-semibold transition-colors">
                {section.secondaryCtaText}
              </button>
            )}
          </div>
        )}

        {section.price && (
          <div className="text-center">
            <div className="text-3xl font-bold text-white">{section.price}</div>
            <p className="text-xs text-slate-400 mt-1">Pricing</p>
          </div>
        )}
      </div>

      {/* Rationale Badges */}
      <div className="bg-slate-900/30 px-4 py-2 border-t border-slate-700/50 flex flex-wrap gap-2">
        <span className="text-xs text-slate-500">SEO: {section.rationale.seo.substring(0, 40)}...</span>
        <span className="text-xs text-slate-500">•</span>
        <span className="text-xs text-slate-500">UX: {section.rationale.ux.substring(0, 30)}...</span>
      </div>
    </div>
  );
};

const PagePreview: React.FC<{ page: MockupPage; index: number }> = ({ page, index }) => {
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-2xl overflow-hidden mb-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-2 h-2 rounded-full bg-indigo-400"></div>
              <h3 className="text-xl font-bold text-white">{page.name}</h3>
              <span className="text-xs text-slate-400 bg-slate-900/50 px-2 py-1 rounded">
                {page.route}
              </span>
            </div>
            <p className="text-sm text-slate-400">{page.description}</p>
            {page.metaTitle && (
              <div className="mt-2 text-xs text-slate-500">
                <span className="font-semibold">SEO:</span> {page.metaTitle}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold text-indigo-400">{page.sections.length}</div>
            <div className="text-xs text-slate-500">Sections</div>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="p-6">
        {page.sections.map((section, idx) => (
          <SectionPreview key={section.id || `section-${idx}`} section={section} index={idx} />
        ))}
      </div>
    </div>
  );
};

export const MockupPreview: React.FC<MockupPreviewProps> = ({ mockup, language }) => {
  // Only Arabic is RTL - Darija uses Latin script, so it's LTR
  const isRTL = language === 'ar';

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Site Overview */}
      <div className="bg-gradient-to-br from-indigo-600/20 via-purple-600/20 to-pink-600/20 border border-slate-700 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-indigo-600 p-3 rounded-xl">
            <Eye className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white">{mockup.siteTitle}</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-2">
              <Palette className="w-4 h-4 text-indigo-400" />
              <h4 className="font-semibold text-white">Color Palette</h4>
            </div>
            <p className="text-sm text-slate-300">{mockup.colorPaletteDescription}</p>
          </div>
          
          <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-2">
              <Type className="w-4 h-4 text-purple-400" />
              <h4 className="font-semibold text-white">Typography</h4>
            </div>
            <p className="text-sm text-slate-300">{mockup.typographyDescription}</p>
          </div>
        </div>

        {mockup.overallStrategy && (
          <div className="mt-4 bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
            <h4 className="font-semibold text-white mb-2">Overall Strategy</h4>
            <p className="text-sm text-slate-300 leading-relaxed">{mockup.overallStrategy}</p>
          </div>
        )}
      </div>

      {/* Pages Preview */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <Layout className="w-5 h-5 text-indigo-400" />
          <h2 className="text-xl font-bold text-white">Website Pages Preview</h2>
          <span className="text-sm text-slate-400">({mockup.pages.length} pages)</span>
        </div>

        {mockup.pages.map((page, idx) => (
          <PagePreview key={page.id || `page-${idx}`} page={page} index={idx} />
        ))}
      </div>
    </div>
  );
};

